<!--
  Moderation Dashboard - Content Type Implementation Status
  
  Currently Implemented (Frontend Ready):
  - questions: Forum questions
  - answers: Forum answers  
  - posts: User posts
  - comments: Comments on content
  
  Backend Ready (Frontend Pending):
  - articles, blogs, challenges, chats, products, links
  - organizations, communities, events, ideas, jobs
  - work_items, tenders, media, entities, people, polls
  
  TODO: Uncomment content types in contentTypeOptions, calculateContentTypeStats, 
  and viewContent methods when frontend implementation is ready.
-->
<template>
  <div class="moderation-dashboard">
    <!-- Header - Full Width -->
    <b-card class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2 class="mb-1">{{ $t('backoffice.moderation.dashboard-title') || 'Panel de Moderación' }}</h2>
          <p class="text-muted mb-0">
            {{ $t('backoffice.moderation.dashboard-subtitle') || 'Revisar y moderar respuestas del foro y comentarios pendientes de aprobación' }}
          </p>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="warning" class="mr-2">
            {{ moderationStats.pending || 0 }} {{ $t('backoffice.moderation.pending-items') || 'Elementos Pendientes' }}
          </b-badge>
          <b-badge variant="success" class="mr-2">
            {{ moderationStats.approved || 0 }} {{ $t('backoffice.moderation.approved-today') || 'Aprobados Hoy' }}
          </b-badge>
          <b-badge variant="danger">
            {{ moderationStats.rejected || 0 }} {{ $t('backoffice.moderation.rejected-today') || 'Rechazados Hoy' }}
          </b-badge>
        </div>
      </div>
    </b-card>

    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="ClockIcon" size="24" class="text-warning mr-2" />
            <div>
              <h4 class="mb-0">{{ moderationStats.pending || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.pending') || 'Pendiente' }}</small>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="CheckCircleIcon" size="24" class="text-success mr-2" />
            <div>
              <h4 class="mb-0">{{ moderationStats.approved || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.approved') || 'Aprobado' }}</small>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="XCircleIcon" size="24" class="text-danger mr-2" />
            <div>
              <h4 class="mb-0">{{ moderationStats.rejected || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.rejected') || 'Rechazado' }}</small>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card class="text-center">
          <div class="d-flex align-items-center justify-content-center">
            <feather-icon icon="UsersIcon" size="24" class="text-info mr-2" />
            <div>
              <h4 class="mb-0">{{ activeModerators || 0 }}</h4>
              <small class="text-muted">{{ $t('backoffice.moderation.active-moderators') || 'Moderadores Activos' }}</small>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2">{{ $t('action.loading') || 'Cargando...' }}</p>
    </div>

    <!-- Content when loaded -->
    <div v-else class="row">
      <!-- Left Column: Filters and Moderators -->
      <div class="col-lg-3 col-md-4 mb-3">
        <!-- Filters Card -->
        <b-card class="mb-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t('backoffice.moderation.filters') || 'Filtros' }}</h5>
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="clearFilters"
            >
              {{ $t('backoffice.moderation.clear-filters') || 'Limpiar' }}
            </b-button>
          </div>
          
          <!-- Search -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.search') || 'Buscar' }}</label>
            <b-input-group>
              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('backoffice.moderation.search-placeholder') || 'Buscar contenido...'"
                @input="onSearchInput"
              />
              <b-input-group-append>
                <b-button variant="outline-secondary" @click="clearSearch">
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <!-- Content Type -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.content-type') || 'Tipo de Contenido' }}</label>
            <b-form-select
              v-model="filters.contentType"
              :options="contentTypeOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Moderation Status -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.status') || 'Estado' }}</label>
            <b-form-select
              v-model="filters.moderationStatus"
              :options="statusOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Forum -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.forum') || 'Foro' }}</label>
            <b-form-select
              v-model="filters.forumKey"
              :options="forumOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Moderator -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.select-moderator') || 'Seleccionar Moderador' }}</label>
            <b-form-select
              v-model="filters.moderatedBy"
              :options="moderatorOptions"
              @change="applyFilters"
            />
          </div>

          <!-- Date Range -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.date-range') || 'Rango de Fechas' }}</label>
            <b-form-datepicker
              v-model="filters.dateFrom"
              :placeholder="$t('backoffice.moderation.from-date') || 'Fecha Desde'"
              @input="applyFilters"
              class="mb-2"
            />
            <b-form-datepicker
              v-model="filters.dateTo"
              :placeholder="$t('backoffice.moderation.to-date') || 'Fecha Hasta'"
              @input="applyFilters"
            />
          </div>

          <!-- Actions -->
          <div class="d-flex gap-2">
            <b-button
              variant="outline-primary"
              @click="refreshData"
              :disabled="isLoading"
              block
            >
              <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
              {{ $t('action.refresh') || 'Actualizar' }}
            </b-button>
          </div>
        </b-card>

        <!-- Performance Metrics Card -->
        <b-card class="mb-3">
          <h5 class="mb-3">{{ $t('backoffice.moderation.performance-metrics') || 'Métricas de Rendimiento' }}</h5>
          
          <div class="row text-center">
            <div class="col-6 mb-3">
              <div class="metric-item">
                <h4 class="text-primary mb-1">{{ averageResponseTime }}m</h4>
                <small class="text-muted">{{ $t('backoffice.moderation.avg-response-time') || 'Tiempo Promedio de Respuesta' }}</small>
              </div>
            </div>
            <div class="col-6 mb-3">
              <div class="metric-item">
                <h4 class="text-success mb-1">{{ totalItemsProcessed }}</h4>
                <small class="text-muted">{{ $t('backoffice.moderation.items-processed') || 'Elementos Procesados' }}</small>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="fetchModeratorPerformance"
              :disabled="isLoading"
              block
            >
              <feather-icon icon="BarChartIcon" size="16" class="mr-1" />
              {{ $t('backoffice.moderation.refresh-metrics') || 'Actualizar Métricas' }}
            </b-button>
          </div>
        </b-card>

        <!-- Content Type Statistics -->
        <b-card class="mb-3">
          <h5 class="mb-3">{{ $t('backoffice.moderation.content-type-stats') || 'Estadísticas por Tipo de Contenido' }}</h5>
          
          <div v-if="contentTypeStats.length > 0">
            <div
              v-for="stat in contentTypeStats"
              :key="stat.type"
              class="content-type-stat mb-2 p-2 border rounded"
              @click="filterByContentType(stat.type)"
              style="cursor: pointer"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <b-badge 
                    :variant="getContentTypeVariant(stat.type)" 
                    class="mr-2"
                  >
                    {{ getContentTypeText(stat.type) }}
                  </b-badge>
                  <span class="font-weight-bold">{{ stat.count }}</span>
                </div>
                <div class="text-right">
                  <small class="text-muted">
                    {{ stat.pending }} {{ $t('backoffice.moderation.pending') || 'Pendiente' }}
                  </small>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-3">
            <feather-icon icon="BarChartIcon" size="32" class="text-muted mb-2" />
            <p class="text-muted mb-0">{{ $t('backoffice.moderation.no-content-stats') || 'No hay estadísticas de contenido disponibles' }}</p>
          </div>
        </b-card>

        <!-- Moderators Card -->
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ $t('backoffice.moderation.moderators') || 'Moderadores' }}</h5>
            <b-badge variant="info">
              {{ staffMembers.length }} {{ $t('backoffice.moderation.available-moderators') || 'Disponibles' }}
            </b-badge>
          </div>
          
          <!-- Moderator Filter -->
          <div class="mb-3">
            <label class="form-label">{{ $t('backoffice.moderation.filter-by-moderator') || 'Filtrar por Moderador' }}</label>
            <b-form-select
              v-model="filters.moderatedBy"
              :options="moderatorFilterOptions"
              @change="applyFilters"
              size="sm"
            />
          </div>

          <!-- Moderator Statistics -->
          <div class="mb-3">
            <h6 class="mb-2">{{ $t('backoffice.moderation.moderator-workload') || 'Carga de Trabajo del Moderador' }}</h6>
            <div v-if="moderatorWorkload.length > 0">
              <div
                v-for="workload in moderatorWorkload"
                :key="workload.moderatorKey"
                class="workload-item p-2 mb-2 border rounded"
                :class="{ 'active-moderator': filters.moderatedBy === workload.moderatorKey }"
                @click="filterByModerator(workload.moderatorKey)"
                style="cursor: pointer"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <b-avatar
                      size="24"
                      :src="getImageSrc(workload.moderator.avatarURL)"
                      class="mr-2"
                    />
                    <span class="font-weight-bold">
                      {{ workload.moderator.name }} {{ workload.moderator.surname }}
                    </span>
                  </div>
                  <div class="text-right">
                    <b-badge variant="primary" class="mr-1">
                      {{ workload.pending }} {{ $t('backoffice.moderation.pending') || 'Pendiente' }}
                    </b-badge>
                    <b-badge variant="success">
                      {{ workload.completed }} {{ $t('backoffice.moderation.completed') || 'Completado' }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="staffMembers.length === 0" class="text-center py-3">
            <feather-icon icon="UsersIcon" size="32" class="text-muted mb-2" />
            <p class="text-muted mb-0">{{ $t('backoffice.moderation.no-moderators') || 'No hay moderadores disponibles' }}</p>
          </div>
          
          <div v-else>
            <div
              v-for="moderator in staffMembers"
              :key="moderator.key"
              class="moderator-card p-3 border rounded mb-2"
            >
              <div class="d-flex align-items-center">
                <b-avatar
                  size="40"
                  :src="getImageSrc(moderator.avatarURL || '')"
                  class="mr-3"
                />
                <div class="flex-grow-1">
                  <div class="font-weight-bold">
                    {{ moderator.name }} {{ moderator.surname }}
                  </div>
                  <small class="text-muted d-block">
                    {{ moderator.email }}
                  </small>
                  <div class="mt-1">
                    <b-badge
                      v-for="role in moderator.roles"
                      :key="role.name"
                      :variant="getRoleVariant(role.name)"
                      class="mr-1"
                    >
                      {{ role.name }}
                    </b-badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Right Column: Content -->
      <div class="col-lg-9 col-md-8">
        <!-- Content Panel -->
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="d-flex align-items-center">
              <h5 class="mb-0 mr-3">{{ $t('backoffice.moderation.content-to-moderate') || 'Contenido a Moderar' }}</h5>
              <b-badge variant="warning" class="mr-2">
                {{ filteredContent.length }} {{ $t('backoffice.moderation.pending-items') || 'Elementos Pendientes' }}
              </b-badge>
            </div>
            <div class="d-flex align-items-center">
              <!-- Bulk Actions -->
              <div v-if="selectedItems.length > 0" class="mr-3">
                <b-dropdown variant="primary" text="Acciones Masivas" class="mr-2">
                  <b-dropdown-item @click="bulkApprove">
                    <feather-icon icon="CheckIcon" size="16" class="mr-1" />
                    {{ $t('backoffice.moderation.bulk-approve') || 'Aprobar Seleccionados' }} ({{ selectedItems.length }})
                  </b-dropdown-item>
                  <b-dropdown-item @click="bulkReject">
                    <feather-icon icon="XIcon" size="16" class="mr-1" />
                    {{ $t('backoffice.moderation.bulk-reject') || 'Rechazar Seleccionados' }} ({{ selectedItems.length }})
                  </b-dropdown-item>
                  <b-dropdown-divider />
                  <b-dropdown-item @click="showBulkAssignModal">
                    <feather-icon icon="UserIcon" size="16" class="mr-1" />
                    {{ $t('backoffice.moderation.bulk-assign') || 'Asignar Moderador' }} ({{ selectedItems.length }})
                  </b-dropdown-item>
                </b-dropdown>
                <b-button variant="outline-secondary" size="sm" @click="clearSelection">
                  {{ $t('backoffice.moderation.clear-selection') || 'Limpiar Selección' }}
                </b-button>
              </div>
              
              <!-- Select All -->
              <b-button
                v-if="filteredContent.length > 0"
                variant="outline-primary"
                size="sm"
                @click="toggleSelectAll"
                class="mr-2"
              >
                {{ isAllSelected ? ($t('backoffice.moderation.deselect-all') || 'Deseleccionar Todo') : ($t('backoffice.moderation.select-all') || 'Seleccionar Todo') }}
              </b-button>
            </div>
          </div>

          <div v-if="filteredContent.length === 0" class="text-center py-5">
            <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-3" />
            <h5>{{ $t('backoffice.moderation.no-pending-content') || 'No hay Contenido Pendiente' }}</h5>
            <p class="text-muted">{{ $t('backoffice.moderation.all-content-moderated') || 'Todo el contenido ha sido moderado' }}</p>
          </div>

          <div v-else>
            <!-- Pagination Controls (Top) -->
            <div v-if="pagination.total > pagination.perPage" class="d-flex justify-content-between align-items-center mb-3">
              <div class="d-flex align-items-center">
                <span class="text-muted mr-3">
                  {{ $t('backoffice.moderation.showing-results', { 
                    from: ((pagination.currentPage - 1) * pagination.perPage) + 1,
                    to: Math.min(pagination.currentPage * pagination.perPage, pagination.total),
                    total: pagination.total 
                  }) || `Showing ${((pagination.currentPage - 1) * pagination.perPage) + 1}-${Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of ${pagination.total} results` }}
                </span>
                <b-form-select
                  v-model="pagination.perPage"
                  :options="[
                    { value: 10, text: '10 por página' },
                    { value: 20, text: '20 por página' },
                    { value: 50, text: '50 por página' },
                    { value: 100, text: '100 por página' }
                  ]"
                  size="sm"
                  @change="onPageSizeChange"
                  style="width: 120px"
                />
              </div>
              <b-pagination
                v-model="pagination.currentPage"
                :total-rows="pagination.total"
                :per-page="pagination.perPage"
                @change="onPageChange"
                size="sm"
              />
            </div>

            <!-- Content Items -->
            <div
              v-for="item in filteredContent"
              :key="item.id"
              class="moderation-item mb-3 p-3 border rounded"
              :class="{ 'selected-item': selectedItems.includes(item.id) }"
            >
              <!-- Item Header -->
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="d-flex align-items-center">
                  <!-- Selection Checkbox -->
                  <b-form-checkbox
                    :value="item.id"
                    v-model="selectedItems"
                    class="mr-3"
                    @change="onItemSelectionChange"
                  />
                  
                  <b-avatar
                    size="32"
                    :src="getImageSrc(item.user && item.user.avatarURL)"
                    class="mr-2"
                  />
                  <div>
                    <strong>
                      {{ item.user && item.user.name }} {{ item.user && item.user.surname }}
                    </strong>
                    <div class="text-muted small">
                      {{ timestampToNow(item.createdAt) }}
                    </div>
                  </div>
                </div>
                <div class="d-flex align-items-center">
                  <b-badge 
                    :variant="getStatusVariant(item.moderationStatus)" 
                    class="mr-2"
                  >
                    {{ getStatusText(item.moderationStatus) }}
                  </b-badge>
                  <b-badge 
                    :variant="getContentTypeVariant(item.contentType)" 
                    class="mr-2"
                  >
                    {{ getContentTypeText(item.contentType) }}
                  </b-badge>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="viewContent(item)"
                  >
                    {{ $t('backoffice.moderation.view-content') || 'Ver Contenido' }}
                  </b-button>
                </div>
              </div>

              <!-- Moderator Assignment Section -->
              <div class="moderator-assignment mb-3 p-3 bg-light rounded">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <feather-icon icon="UserIcon" size="16" class="mr-2 text-muted" />
                    <span class="font-weight-bold mr-2">
                      {{ $t('backoffice.moderation.assigned-moderator') || 'Moderador Asignado' }}:
                    </span>
                    <div v-if="item.assignedModerator" class="d-flex align-items-center">
                      <b-avatar
                        size="24"
                        :src="getImageSrc(item.assignedModerator.avatarURL)"
                        class="mr-2"
                      />
                      <span class="text-success">
                        {{ item.assignedModerator.name }} {{ item.assignedModerator.surname }}
                      </span>
                      <b-badge variant="success" class="ml-2">
                        {{ $t('backoffice.moderation.assigned') || 'Asignado' }}
                      </b-badge>
                    </div>
                    <div v-else class="text-warning">
                      <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
                      {{ $t('backoffice.moderation.no-moderator-assigned') || 'Sin moderador asignado' }}
                    </div>
                  </div>
                  
                  <!-- Moderator Selector -->
                  <div class="moderator-selector">
                    <b-form-select
                      :value="item.assignedModerator ? item.assignedModerator.key : ''"
                      :options="moderatorOptions"
                      @change="updateModeratorAssignment(item, $event)"
                      size="sm"
                      style="min-width: 200px"
                    />
                  </div>
                </div>
              </div>

              <!-- Item Content -->
              <div class="mb-3">
                <template v-if="item.type === 'answer'">
                  <h6 class="mb-2">
                    {{ $t('backoffice.moderation.answer-to') || 'Respuesta a' }}:
                  </h6>
                  <h6 class="mb-2">
                    {{ $t('backoffice.moderation.forum') || 'Foro' }}: {{ getForumName(item.forumKey) }}
                  </h6>
                  <div v-html="translate(item.description)" class="content-preview" />
                </template>
                <template v-else>
                  <h6 class="mb-2">{{ $t('backoffice.moderation.comment-on') || 'Comentario en' }}:</h6>
                  <div class="mb-2">
                    <span class="text-muted">{{ getCommentSourceInfo(item) }}</span>
                  </div>
                  <div v-if="item.content" v-html="item.content" class="content-preview" />
                  <div v-else class="text-muted">
                    <em>{{ $t('backoffice.moderation.no-content-available') || 'No hay contenido disponible' }}</em>
                  </div>
                </template>
              </div>


              <!-- Moderation History -->
              <div v-if="item.moderationHistory && item.moderationHistory.length > 0" class="mb-3">
                <h6 class="mb-2">
                  <feather-icon icon="ClockIcon" size="16" class="mr-1" />
                  {{ $t('backoffice.moderation.moderation-history') || 'Moderation History' }}
                </h6>
                <div class="moderation-history">
                  <div
                    v-for="(history, index) in item.moderationHistory"
                    :key="index"
                    class="history-item p-2 mb-2 border-left border-3"
                    :class="getHistoryItemClass(history.action)"
                  >
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{{ getHistoryActionText(history.action) }}</strong>
                        <span class="text-muted ml-2">
                          {{ timestampToNow(history.timestamp) }}
                        </span>
                      </div>
                      <b-badge :variant="getHistoryBadgeVariant(history.action)">
                        {{ getHistoryActionText(history.action) }}
                      </b-badge>
                    </div>
                    <div v-if="history.moderator" class="mt-1">
                      <small class="text-muted">
                        {{ $t('backoffice.moderation.moderated-by') || 'Moderated by' }}: 
                        {{ history.moderator.name }} {{ history.moderator.surname }}
                      </small>
                    </div>
                    <div v-if="history.feedback" class="mt-1">
                      <small class="text-muted">
                        {{ $t('backoffice.moderation.feedback') || 'Feedback' }}: {{ history.feedback }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Use existing ModerationSection component -->
              <moderation-section
                :content="item"
                :morph-type="item.type === 'answer' ? 'forums' : (item.morphType || 'comment')"
                :item-type="item.type === 'answer' ? 'forumAnswers' : 'comments'"
                :item-type-name="item.type === 'answer' ? 'answer' : 'comment'"
                :parent-key="item.type === 'answer' ? item.forumKey : getCommentParentKey(item)"
                :moderator-options="moderatorOptions"
                :on-assign-moderator="assignModerator"
                :can-moderate=true
                @moderation-complete="handleModerationComplete"
              />
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Bulk Assign Moderator Modal -->
    <b-modal
      id="bulk-assign-moderator-modal"
      :title="$t('backoffice.moderation.bulk-assign-moderator') || 'Assign Moderator to Selected Items'"
      centered
    >
      <div class="mb-3">
        <p>{{ $t('backoffice.moderation.bulk-assign-description', { count: selectedItems.length }) || `Assign a moderator to ${selectedItems.length} selected items` }}</p>
      </div>
      
      <b-form-group :label="$t('backoffice.moderation.select-moderator') || 'Select Moderator'">
        <b-form-select
          v-model="bulkAssignModerator"
          :options="moderatorOptions"
          :state="bulkAssignModerator ? null : false"
        />
      </b-form-group>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('bulk-assign-moderator-modal')"
        >
          {{ $t('action.cancel') || 'Cancel' }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!bulkAssignModerator"
          @click="confirmBulkAssignModerator(bulkAssignModerator)"
        >
          {{ $t('backoffice.moderation.assign') || 'Assign Moderator' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { getImageResource } from '@/@core/utils/image-utils';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import ModerationSection from '@/@core/components/moderation/ModerationSection.vue';

export default {
  name: 'ModerationDashboard',
  
  components: {
    ModerationSection
  },

  mixins: [ToastNotificationsMixin],

  data() {
    return {
      isLoading: true,
      searchQuery: '',
      searchTimeout: null,
      selectedItems: [],
      moderationQueue: [],
      moderationStats: {
        pending: 0,
        approved: 0,
        rejected: 0
      },
      activeModerators: 0,
      filters: {
        contentType: 'all',
        moderationStatus: 'pending',
        forumKey: '',
        moderatedBy: '',
        dateFrom: null,
        dateTo: null
      },
      pagination: {
        currentPage: 1,
        perPage: 20,
        total: 0
      },
      forums: [],
      staffMembers: [],
      bulkAssignModerator: '',
      moderatorPerformance: [],
      averageResponseTime: 0,
      totalItemsProcessed: 0,
      updateInterval: null,
      moderatorWorkload: [],
      contentTypeStats: []
    };
  },

  computed: {
    collective() {
      return this.$store.state.collective.collective;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    isAllSelected() {
      return this.filteredContent.length > 0 && this.selectedItems.length === this.filteredContent.length;
    },
    contentTypeOptions() {
      return [
        { value: 'all', text: this.$t('backoffice.moderation.all-content') || 'Todo el Contenido' },
        { value: 'questions', text: this.$t('backoffice.moderation.questions') || 'Preguntas del Foro' },
        { value: 'answers', text: this.$t('backoffice.moderation.answers') || 'Respuestas del Foro' },
        { value: 'posts', text: this.$t('backoffice.moderation.posts') || 'Publicaciones' },
        { value: 'comments', text: this.$t('backoffice.moderation.comments') || 'Comentarios' }
        // TODO: Uncomment when frontend implementation is ready
        // { value: 'articles', text: this.$t('backoffice.moderation.articles') || 'Articles' },
        // { value: 'blogs', text: this.$t('backoffice.moderation.blogs') || 'Blogs' },
        // { value: 'challenges', text: this.$t('backoffice.moderation.challenges') || 'Challenges' },
        // { value: 'chats', text: this.$t('backoffice.moderation.chats') || 'Chats' },
        // { value: 'products', text: this.$t('backoffice.moderation.products') || 'Products' },
        // { value: 'links', text: this.$t('backoffice.moderation.links') || 'Links' },
        // { value: 'organizations', text: this.$t('backoffice.moderation.organizations') || 'Organizations' },
        // { value: 'communities', text: this.$t('backoffice.moderation.communities') || 'Communities' },
        // { value: 'events', text: this.$t('backoffice.moderation.events') || 'Events' },
        // { value: 'ideas', text: this.$t('backoffice.moderation.ideas') || 'Ideas' },
        // { value: 'jobs', text: this.$t('backoffice.moderation.jobs') || 'Jobs' },
        // { value: 'work_items', text: this.$t('backoffice.moderation.work-items') || 'Work Items' },
        // { value: 'tenders', text: this.$t('backoffice.moderation.tenders') || 'Tenders' },
        // { value: 'media', text: this.$t('backoffice.moderation.media') || 'Media' },
        // { value: 'entities', text: this.$t('backoffice.moderation.entities') || 'Entities' },
        // { value: 'people', text: this.$t('backoffice.moderation.people') || 'People' },
        // { value: 'polls', text: this.$t('backoffice.moderation.polls') || 'Polls' }
      ];
    },
    statusOptions() {
      return [
        { value: 'pending', text: this.$t('backoffice.moderation.pending') || 'Pendiente' },
        { value: 'approved', text: this.$t('backoffice.moderation.approved') || 'Aprobado' },
        { value: 'rejected', text: this.$t('backoffice.moderation.rejected') || 'Rechazado' },
        { value: 'all', text: this.$t('backoffice.moderation.all-statuses') || 'Todos los Estados' }
      ];
    },
    forumOptions() {
      const options = [
        { value: '', text: this.$t('backoffice.moderation.all-forums') || 'Todos los Foros' }
      ];
      
      this.forums.forEach(forum => {
        options.push({
          value: forum.key,
          text: this.translate(forum.name)
        });
      });
      
      return options;
    },
    filteredContent() {
      // Use the moderationQueue directly since it contains the API response data
      let content = [...this.moderationQueue];
      
      // Apply content type filter
      if (this.filters.contentType !== 'all') {
        content = content.filter(item => item.contentType === this.filters.contentType);
      }
      
      // Apply status filter
      if (this.filters.moderationStatus !== 'all') {
        content = content.filter(item => item.moderationStatus === this.filters.moderationStatus);
      }
      
      // Apply forum filter
      if (this.filters.forumKey !== '') {
        content = content.filter(item => item.mainSpaceID === this.filters.forumKey);
      }
      
      // Apply moderator filter
      if (this.filters.moderatedBy !== '') {
        content = content.filter(item => item.moderatedBy === this.filters.moderatedBy);
      }
      
      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        content = content.filter(item => 
          (item.contentKey && item.contentKey.toLowerCase().includes(query)) ||
          (item.user && item.user.name && item.user.name.toLowerCase().includes(query)) ||
          (item.user && item.user.surname && item.user.surname.toLowerCase().includes(query))
        );
      }
      
      // Sort by creation date (newest first)
      return content.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    filteredPendingAnswers() {
      let answers = this.pendingAnswers;
      
      // Filter to show only items with moderationStatus == 0 (pending moderation)
      answers = answers.filter(answer => answer.moderationStatus === 0);
      
      if (this.selectedForum !== 'all') {
        answers = answers.filter(answer => answer.forumKey === this.selectedForum);
      }
      
      if (this.selectedModerator !== '') {
        answers = answers.filter(answer => answer.assignedModeratorKey === this.selectedModerator);
      }
      
      return answers;
    },
    filteredPendingComments() {
      let comments = this.pendingComments;
      
      // Filter to show only items with moderationStatus == 0 (pending moderation)
      comments = comments.filter(comment => comment.moderationStatus === 0);
      
      if (this.selectedForum !== 'all') {
        comments = comments.filter(comment => {
          // Now we have forumKey directly on the comment
          return comment.forumKey === this.selectedForum;
        });
      }
      
      if (this.selectedModerator !== '') {
        comments = comments.filter(comment => comment.assignedModeratorKey === this.selectedModerator);
      }
      
      return comments;
    },
    moderatorOptions() {
      const options = [
        { value: '', text: this.$t('backoffice.moderation.select-moderator') || 'Seleccionar Moderador' }
      ];
      
      this.staffMembers.forEach(member => {
        options.push({
          value: member.key,
          text: `${member.name} ${member.surname}`,
          member: member
        });
      });
      
      return options;
    },
    moderatorFilterOptions() {
      const options = [
        { value: '', text: this.$t('backoffice.moderation.all-moderators') || 'Todos los Moderadores' },
        { value: 'unassigned', text: this.$t('backoffice.moderation.unassigned') || 'Sin Asignar' }
      ];
      
      this.staffMembers.forEach(member => {
        options.push({
          value: member.key,
          text: `${member.name} ${member.surname}`,
          member: member
        });
      });
      
      return options;
    }
  },

  async created() {
    if (!this.isStaff) {
      this.$router.push({ name: 'backoffice' });
      return;
    }
    
    await this.fetchData();
    this.updateBreadcrumbs();
    this.setupRealTimeUpdates();
  },

  beforeDestroy() {
    this.stopRealTimeUpdates();
  },

  methods: {
    getImageSrc(url) {
      return getImageResource(url);
    },
    translate(field) {
      return translateTranslationTable(this.currentLocale, field);
    },
    timestampToNow(timestamp) {
      moment.locale(this.currentLocale);
      return moment(timestamp).format('DD/MM/YYYY HH:mm');
    },
    getForumName(forumKey) {
      const forum = this.forums.find(f => f.key === forumKey);
      return forum ? this.translate(forum.name) : (this.$t('backoffice.moderation.unknown-forum') || 'Foro Desconocido');
    },
    getForumAnswerName(forumAnswerKey) {
      const forumAnswer = this.pendingAnswers.find(f => f.key === forumAnswerKey);
      return forumAnswer ? this.translate(forumAnswer.name) : (this.$t('backoffice.moderation.unknown-forum-answer') || 'Respuesta de Foro Desconocida');
    },
    getCommentSourceInfo(comment) {
      // Try to get source information from different possible fields
      if (comment.morphType === 'answer' && comment.forumKey) {
        return `${this.$t('backoffice.moderation.comment-to') || 'Comentario a'} "${this.getForumAnswerName(comment.forumKey)}"`;
      }
      
      // Check for other source information
      if (comment.sourceTitle) {
        return `${this.$t('backoffice.moderation.comment-on') || 'Comentario en'}: "${comment.sourceTitle}"`;
      }
      
      if (comment.parentTitle) {
        return `${this.$t('backoffice.moderation.comment-on') || 'Comentario en'}: "${comment.parentTitle}"`;
      }
      
      if (comment.sourceKey) {
        return `${this.$t('backoffice.moderation.comment-on') || 'Comentario en'}: ${comment.sourceKey}`;
      }
      
      // If we have content, show a preview instead of "Unknown source"
      if (comment.content && comment.content.trim()) {
        const contentPreview = comment.content.length > 100 
          ? comment.content.substring(0, 100) + '...' 
          : comment.content;
        return `${this.$t('backoffice.moderation.comment-content') || 'Contenido del comentario'}: "${contentPreview}"`;
      }
      
      return this.$t('backoffice.moderation.unknown-source') || 'Fuente desconocida';
    },
    getCommentParentKey(comment) {
      // For comments on answers, use the answer key as parent
      if (comment.morphType === 'answer') {
        return comment.key;
      }
      // For other comment types, use the source key
      return comment.sourceKey || comment.key;
    },
    async fetchStaffMembers() {
      try {
        // Fetch staff members (response structure may vary)
        const response = await this.$store.dispatch('getItems', {
          itemType: 'backofficeStaff',
          storedKey: 'backofficeStaff',
          customName: 'communityMembers',
          forceAPICall: true,
          page: 1,
          perPage: 100,
          requestConfig: {
            isStaff: 1,
            isForBackoffice: 1,
          }
        });

        // Normalize response to an array
        let rawMembers = [];
        if (response && Array.isArray(response.data)) {
          rawMembers = response.data;
        } else if (Array.isArray(response)) {
          rawMembers = response;
        } else if (response && Array.isArray(response.items)) {
          rawMembers = response.items;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          rawMembers = response.data.data;
        } else {
          console.warn('Unexpected staff members response structure:', response);
        }

        // Filter to get only staff members (fallback to role check if needed)
        this.staffMembers = (rawMembers || []).filter(member => {
          if (!member) return false;
          if (member.isStaff) return true;
          const roles = member.roles || [];
          return roles.some(role => role && (role.name === 'Owner' || role.name === 'Moderator' || role.name === 'Admin'));
        });

        console.log('Fetched staff members:', this.staffMembers.length);
      } catch (error) {
        console.error('Error fetching staff members:', error);
        this.staffMembers = [];
      }
    },
    async fetchData() {
      this.isLoading = true;
      
      try {
        // Fetch data in parallel for better performance
        const promises = [
          this.fetchModerationQueue(),
          this.fetchStaffMembers(),
          this.fetchForums()
        ];
        
        // Wait for all promises to resolve, but don't fail if some fail
        await Promise.allSettled(promises);
        
        // Calculate stats from the moderation queue data
        this.calculateModerationStats();
        
        // Show a general success message if at least some data was fetched
        if (this.moderationQueue.length > 0 || this.staffMembers.length > 0) {
          console.log('Moderation data loaded successfully');
        }
        
      } catch (error) {
        console.error('Error fetching moderation data:', error);
        this.notifyError(this.$t('error-message.general-error') || 'An error occurred while loading moderation data');
      } finally {
        this.isLoading = false;
      }
    },

    async fetchModerationQueue() {
      try {
        // Filter out empty values from filters
        const cleanFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => 
            value !== '' && value !== null && value !== undefined
          )
        );
        
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderation',
          storedKey: 'queue',
          forceAPICall: true,
          page: this.pagination.currentPage,
          perPage: this.pagination.perPage,
          requestConfig: {
            ...cleanFilters,
            count: this.pagination.perPage,
            offset: (this.pagination.currentPage - 1) * this.pagination.perPage
          }
        });

        this.moderationQueue = response?.data || [];
        this.pagination.total = response?.total || 0;
        
        // Calculate stats from the fetched data
        this.calculateModerationStats();
        
        // Calculate moderator workload after fetching data
        await this.calculateModeratorWorkload();
        
        // Calculate content type statistics
        await this.calculateContentTypeStats();
        
        console.log('Fetched moderation queue:', this.moderationQueue.length);
      } catch (error) {
        console.error('Error fetching moderation queue:', error);
        this.moderationQueue = [];
        this.pagination.total = 0;
        
        // Handle specific error types
        if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
          this.notifyError(this.$t('backoffice.moderation.network-error') || 'Network error. Please check your connection and try again.');
        } else if (error.response?.status === 503) {
          this.notifyError(this.$t('backoffice.moderation.service-unavailable') || 'Moderation service is temporarily unavailable. Please try again later.');
        } else if (error.response?.status === 403) {
          this.notifyError(this.$t('backoffice.moderation.access-denied') || 'Access denied. You may not have permission to access moderation data.');
        } else {
          this.notifyError(this.$t('backoffice.moderation.fetch-error') || 'Error fetching moderation data. Please try again.');
        }
      }
    },

    calculateModerationStats() {
      try {
        // Calculate stats from the existing moderation queue data
        if (this.moderationQueue && Array.isArray(this.moderationQueue)) {
          this.moderationStats = {
            pending: this.moderationQueue.filter(item => item.moderationStatus === 0 || item.moderationStatus === 'pending').length,
            approved: this.moderationQueue.filter(item => item.moderationStatus === 1 || item.moderationStatus === 'approved').length,
            rejected: this.moderationQueue.filter(item => item.moderationStatus === 2 || item.moderationStatus === 'rejected').length
          };
        } else {
          this.moderationStats = {
            pending: 0,
            approved: 0,
            rejected: 0
          };
        }
        
        console.log('Calculated moderation stats:', this.moderationStats);
      } catch (error) {
        console.error('Error calculating moderation stats:', error);
        this.moderationStats = {
          pending: 0,
          approved: 0,
          rejected: 0
        };
      }
    },

    async fetchForums() {
      try {
        await this.$store.dispatch('getItems', {
          itemType: 'forums',
          storedKey: 'list',
          forceAPICall: true,
          page: 1,
          perPage: 100
        });
        
        const forumsData = this.$store.getters.forums;
        this.forums = forumsData.list?.unpaginated || [];
      } catch (error) {
        console.error('Error fetching forums:', error);
        this.forums = [];
      }
    },
    async refreshData() {
      await this.fetchData();
      this.notifySuccess(this.$t('backoffice.moderation.data-refreshed') || 'Moderation data refreshed successfully');
    },
    async handleModerationComplete({ action, contentKey, response }) {
      // Show success notification
      this.notifySuccess(
        this.$t(`backoffice.moderation.success-${action}`) || `Action ${action} completed successfully`
      );

      // Refresh data to update the list
      await this.fetchData();
    },
    viewForum(forumKey) {
      this.$router.push({ name: 'forum', params: { id: forumKey } });
    },
    viewCommentSource(comment) {
      // Navigate to the source of the comment
      if (comment.morphType === 'answer' && comment.forumKey) {
        this.viewForum(comment.forumKey);
      }
    },
    updateBreadcrumbs() {
      const breadcrumbs = [
        {
          text: this.$t('backoffice.title') || 'Backoffice',
          to: { name: 'backoffice' }
        },
        {
          text: this.$t('backoffice.moderation.dashboard-title') || 'Moderation Dashboard',
          active: true
        }
      ];
      this.$store.commit('app/SET_BREADCRUMBS', breadcrumbs);
    },
    async assignModerator(content, moderatorKey) {
      try {
        // Update the content with assigned moderator
        const response = await this.$store.dispatch('editItem', {
          item: {
            itemType: content.morphType === 'answer' ? 'forumAnswers' : 'comments',
            morphType: content.morphType || 'forums',
            requestConfig: {
              [`${content.morphType === 'answer' ? 'forumAnswer' : 'comment'}Key`]: content.key,
              parentKey: content.morphType === 'answer' ? content.forumKey : content.answerKey,
              assignedModeratorKey: moderatorKey
            },
          },
        });

        // Update local data
        if (content.morphType === 'answer') {
          const answerIndex = this.pendingAnswers.findIndex(a => a.key === content.key);
          if (answerIndex !== -1) {
            this.pendingAnswers[answerIndex].assignedModeratorKey = moderatorKey;
          }
        } else {
          const commentIndex = this.pendingComments.findIndex(c => c.key === content.key);
          if (commentIndex !== -1) {
            this.pendingComments[commentIndex].assignedModeratorKey = moderatorKey;
          }
        }

        this.notifySuccess(this.$t('backoffice.moderation.moderator-assigned') || 'Moderator assigned successfully');
      } catch (error) {
        console.error('Error assigning moderator:', error);
        this.notifyError(this.$t('backoffice.moderation.moderator-assign-error') || 'Error assigning moderator');
      }
    },
    getModeratorName(moderatorKey) {
      const moderator = this.staffMembers.find(m => m.key === moderatorKey);
      return moderator ? `${moderator.name} ${moderator.surname}` : '';
    },
    getSelectedModeratorData() {
      return this.staffMembers.find(m => m.key === this.selectedModerator);
    },
    getRoleVariant(roleName) {
      switch (roleName) {
        case 'Owner':
          return 'warning';
        case 'Admin':
          return 'danger';
        case 'Moderator':
          return 'info';
        default:
          return 'secondary';
      }
    },
    // New methods for enhanced functionality
    async applyFilters() {
      this.pagination.currentPage = 1;
      await this.fetchModerationQueue();
    },

    onSearchInput() {
      // Debounce search input
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },

    clearSearch() {
      this.searchQuery = '';
      this.applyFilters();
    },

    clearFilters() {
      this.filters = {
        contentType: 'all',
        moderationStatus: 'pending',
        forumKey: '',
        moderatedBy: '',
        dateFrom: null,
        dateTo: null
      };
      this.searchQuery = '';
      this.applyFilters();
    },

    // Bulk moderation methods
    async bulkApprove() {
      if (this.selectedItems.length === 0) return;
      
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationBulk',
            requestConfig: {
              moderationIDs: this.selectedItems,
              moderationStatus: 'approved'
            }
          }
        });

        this.notifySuccess(
          this.$t('backoffice.moderation.bulk-approve-success', { count: this.selectedItems.length }) || 
          `Successfully approved ${this.selectedItems.length} items`
        );
        
        this.clearSelection();
        await this.fetchData();
      } catch (error) {
        console.error('Error in bulk approve:', error);
        this.notifyError(this.$t('backoffice.moderation.bulk-approve-error') || 'Error approving items');
      }
    },

    async bulkReject() {
      if (this.selectedItems.length === 0) return;
      
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationBulk',
            requestConfig: {
              moderationIDs: this.selectedItems,
              moderationStatus: 'rejected'
            }
          }
        });

        this.notifySuccess(
          this.$t('backoffice.moderation.bulk-reject-success', { count: this.selectedItems.length }) || 
          `Successfully rejected ${this.selectedItems.length} items`
        );
        
        this.clearSelection();
        await this.fetchData();
      } catch (error) {
        console.error('Error in bulk reject:', error);
        this.notifyError(this.$t('backoffice.moderation.bulk-reject-error') || 'Error rejecting items');
      }
    },

    async showBulkAssignModal() {
      if (this.selectedItems.length === 0) return;
      
      // Show modal to select moderator
      this.$bvModal.show('bulk-assign-moderator-modal');
    },

    async confirmBulkAssignModerator(moderatorKey) {
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationBulk',
            requestConfig: {
              moderationIDs: this.selectedItems,
              moderatedBy: moderatorKey
            }
          }
        });

        this.notifySuccess(
          this.$t('backoffice.moderation.bulk-assign-success', { count: this.selectedItems.length }) || 
          `Successfully assigned moderator to ${this.selectedItems.length} items`
        );
        
        this.clearSelection();
        await this.fetchData();
        this.$bvModal.hide('bulk-assign-moderator-modal');
      } catch (error) {
        console.error('Error in bulk assign moderator:', error);
        this.notifyError(this.$t('backoffice.moderation.bulk-assign-error') || 'Error assigning moderator');
      }
    },

    // Selection methods
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.clearSelection();
      } else {
        this.selectedItems = this.filteredContent.map(item => item.id);
      }
    },

    clearSelection() {
      this.selectedItems = [];
    },

    onItemSelectionChange() {
      // Handle individual item selection changes
    },

    // Utility methods
    getStatusVariant(status) {
      switch (status) {
        case 'pending': return 'warning';
        case 'approved': return 'success';
        case 'rejected': return 'danger';
        default: return 'secondary';
      }
    },

    getStatusText(status) {
      switch (status) {
        case 'pending': return this.$t('backoffice.moderation.pending') || 'Pendiente';
        case 'approved': return this.$t('backoffice.moderation.approved') || 'Aprobado';
        case 'rejected': return this.$t('backoffice.moderation.rejected') || 'Rechazado';
        default: return status;
      }
    },

    getContentTypeVariant(contentType) {
      const variants = {
        'questions': 'primary',
        'answers': 'info',
        'posts': 'success',
        'comments': 'secondary',
        'articles': 'warning',
        'blogs': 'dark',
        'challenges': 'danger',
        'chats': 'light',
        'products': 'primary',
        'links': 'info',
        'organizations': 'success',
        'communities': 'warning',
        'events': 'dark',
        'ideas': 'danger',
        'jobs': 'light',
        'work_items': 'primary',
        'tenders': 'info',
        'media': 'success',
        'entities': 'warning',
        'people': 'dark',
        'polls': 'danger'
      };
      return variants[contentType] || 'secondary';
    },

    getContentTypeText(contentType) {
      const texts = {
        'questions': this.$t('backoffice.moderation.questions') || 'Preguntas',
        'answers': this.$t('backoffice.moderation.answers') || 'Respuestas',
        'posts': this.$t('backoffice.moderation.posts') || 'Publicaciones',
        'comments': this.$t('backoffice.moderation.comments') || 'Comentarios',
        'articles': this.$t('backoffice.moderation.articles') || 'Artículos',
        'blogs': this.$t('backoffice.moderation.blogs') || 'Blogs',
        'challenges': this.$t('backoffice.moderation.challenges') || 'Desafíos',
        'chats': this.$t('backoffice.moderation.chats') || 'Chats',
        'products': this.$t('backoffice.moderation.products') || 'Productos',
        'links': this.$t('backoffice.moderation.links') || 'Enlaces',
        'organizations': this.$t('backoffice.moderation.organizations') || 'Organizaciones',
        'communities': this.$t('backoffice.moderation.communities') || 'Comunidades',
        'events': this.$t('backoffice.moderation.events') || 'Eventos',
        'ideas': this.$t('backoffice.moderation.ideas') || 'Ideas',
        'jobs': this.$t('backoffice.moderation.jobs') || 'Empleos',
        'work_items': this.$t('backoffice.moderation.work-items') || 'Elementos de Trabajo',
        'tenders': this.$t('backoffice.moderation.tenders') || 'Licitaciones',
        'media': this.$t('backoffice.moderation.media') || 'Medios',
        'entities': this.$t('backoffice.moderation.entities') || 'Entidades',
        'people': this.$t('backoffice.moderation.people') || 'Personas',
        'polls': this.$t('backoffice.moderation.polls') || 'Encuestas'
      };
      return texts[contentType] || contentType;
    },

    viewContent(item) {
      console.log('viewContent - item:', item);
      // Navigate to content based on content type (only implemented types) - opens in new tab
      const navigationMap = {
        'questions': () => this.openInNewTab('forum', { id: item.contentKey }),
        'answers': () => this.openInNewTab('forum', { id: item.forumKey }),
        'posts': () => this.openInNewTab('posts', { id: item.contentKey }),
        'comments': () => this.openInNewTab('comments', { id: item.contentKey })
        // TODO: Add navigation for other content types when frontend implementation is ready
        // 'articles': () => this.openInNewTab('articles', { id: item.contentKey }),
        // 'blogs': () => this.openInNewTab('blogs', { id: item.contentKey }),
        // 'challenges': () => this.openInNewTab('challenges', { id: item.contentKey }),
        // 'chats': () => this.openInNewTab('chats', { id: item.contentKey }),
        // 'products': () => this.openInNewTab('products', { id: item.contentKey }),
        // 'links': () => this.openInNewTab('links', { id: item.contentKey }),
        // 'organizations': () => this.openInNewTab('organizations', { id: item.contentKey }),
        // 'communities': () => this.openInNewTab('communities', { id: item.contentKey }),
        // 'events': () => this.openInNewTab('events', { id: item.contentKey }),
        // 'ideas': () => this.openInNewTab('ideas', { id: item.contentKey }),
        // 'jobs': () => this.openInNewTab('jobs', { id: item.contentKey }),
        // 'work_items': () => this.openInNewTab('work-items', { id: item.contentKey }),
        // 'tenders': () => this.openInNewTab('tenders', { id: item.contentKey }),
        // 'media': () => this.openInNewTab('media', { id: item.contentKey }),
        // 'entities': () => this.openInNewTab('entities', { id: item.contentKey }),
        // 'people': () => this.openInNewTab('people', { id: item.contentKey }),
        // 'polls': () => this.openInNewTab('polls', { id: item.contentKey })
      };

      const navigate = navigationMap[item.contentType];
      if (navigate) {
        try {
          navigate();
        } catch (error) {
          console.warn(`Navigation not available for content type: ${item.contentType}`);
          // Fallback: show content in modal
          this.$bvModal.show('content-preview-modal');
        }
      } else {
        // Fallback: show content in modal
        this.$bvModal.show('content-preview-modal');
      }
    },

    // Helper method to open content in new tab
    openInNewTab(routeName, params) {
      try {
        // Generate the URL using Vue Router's resolve method
        const route = this.$router.resolve({ name: routeName, params });
        if (route && route.href) {
          window.open(route.href, '_blank');
        } else {
          console.warn(`Could not resolve route for ${routeName} with params:`, params);
          this.$bvModal.show('content-preview-modal');
        }
      } catch (error) {
        console.error(`Error opening ${routeName} in new tab:`, error);
        this.$bvModal.show('content-preview-modal');
      }
    },

    // Pagination methods
    async onPageChange(page) {
      this.pagination.currentPage = page;
      await this.fetchModerationQueue();
      this.scrollToTop();
    },

    async onPageSizeChange() {
      this.pagination.currentPage = 1;
      await this.fetchModerationQueue();
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // Lazy loading and performance optimization
    async loadMoreItems() {
      if (this.isLoading || this.pagination.currentPage * this.pagination.perPage >= this.pagination.total) {
        return;
      }

      this.isLoading = true;
      try {
        const nextPage = this.pagination.currentPage + 1;
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderation',
          storedKey: 'queue',
          forceAPICall: true,
          page: nextPage,
          perPage: this.pagination.perPage,
          requestConfig: {
            ...this.filters,
            count: this.pagination.perPage,
            offset: (nextPage - 1) * this.pagination.perPage
          }
        });

        // Append new items to existing queue
        this.moderationQueue.push(...(response.data || []));
        this.pagination.currentPage = nextPage;
      } catch (error) {
        console.error('Error loading more items:', error);
        this.notifyError(this.$t('backoffice.moderation.load-more-error') || 'Error loading more items');
      } finally {
        this.isLoading = false;
      }
    },

    // Virtual scrolling for large datasets
    setupVirtualScrolling() {
      // This would be implemented with a virtual scrolling library
      // For now, we'll use pagination as the primary optimization
    },

    // Cache management
    clearCache() {
      // Clear cached moderation data
      this.$store.commit('moderation/CLEAR_CACHE');
    },

    // Performance monitoring
    trackPerformance(action, duration) {
      console.log(`Performance: ${action} took ${duration}ms`);
      // Could send to analytics service
    },

    // Audit trail methods
    getHistoryItemClass(action) {
      switch (action) {
        case 'approved': return 'border-success bg-light-success';
        case 'rejected': return 'border-danger bg-light-danger';
        case 'assigned': return 'border-info bg-light-info';
        case 'escalated': return 'border-warning bg-light-warning';
        default: return 'border-secondary bg-light';
      }
    },

    getHistoryActionText(action) {
      switch (action) {
        case 'approved': return this.$t('backoffice.moderation.approved') || 'Approved';
        case 'rejected': return this.$t('backoffice.moderation.rejected') || 'Rejected';
        case 'assigned': return this.$t('backoffice.moderation.assigned') || 'Assigned';
        case 'escalated': return this.$t('backoffice.moderation.escalated') || 'Escalated';
        default: return action;
      }
    },

    getHistoryBadgeVariant(action) {
      switch (action) {
        case 'approved': return 'success';
        case 'rejected': return 'danger';
        case 'assigned': return 'info';
        case 'escalated': return 'warning';
        default: return 'secondary';
      }
    },

    async fetchModerationHistory(itemId) {
      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderationHistory',
          storedKey: `history-${itemId}`,
          forceAPICall: true,
          requestConfig: {
            moderationId: itemId
          }
        });

        return response.data || [];
      } catch (error) {
        console.error('Error fetching moderation history:', error);
        return [];
      }
    },

    // Performance metrics methods
    async fetchModeratorPerformance() {
      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderatorPerformance',
          storedKey: 'performance',
          forceAPICall: true,
          requestConfig: {
            dateFrom: this.filters.dateFrom,
            dateTo: this.filters.dateTo
          }
        });

        this.moderatorPerformance = response.data || [];
        this.calculatePerformanceMetrics();
      } catch (error) {
        console.error('Error fetching moderator performance:', error);
        this.moderatorPerformance = [];
      }
    },

    calculatePerformanceMetrics() {
      if (!this.moderatorPerformance.length) return;

      // Calculate average response time
      const totalResponseTime = this.moderatorPerformance.reduce((sum, mod) => 
        sum + (mod.averageResponseTime || 0), 0);
      this.averageResponseTime = totalResponseTime / this.moderatorPerformance.length;

      // Calculate total items processed
      this.totalItemsProcessed = this.moderatorPerformance.reduce((sum, mod) => 
        sum + (mod.itemsProcessed || 0), 0);

      // Calculate active moderators
      this.activeModerators = this.moderatorPerformance.filter(mod => 
        mod.itemsProcessed > 0).length;
    },

    // Workflow improvements
    async escalateItem(itemId, reason) {
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationEscalate',
            requestConfig: {
              moderationId: itemId,
              reason: reason,
              escalatedBy: this.$store.state.auth.user.id
            }
          }
        });

        this.notifySuccess(this.$t('backoffice.moderation.escalated-success') || 'Item escalated successfully');
        await this.fetchData();
      } catch (error) {
        console.error('Error escalating item:', error);
        this.notifyError(this.$t('backoffice.moderation.escalate-error') || 'Error escalating item');
      }
    },

    async addModerationNote(itemId, note) {
      try {
        await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationNote',
            requestConfig: {
              moderationId: itemId,
              note: note,
              addedBy: this.$store.state.auth.user.id
            }
          }
        });

        this.notifySuccess(this.$t('backoffice.moderation.note-added') || 'Note added successfully');
      } catch (error) {
        console.error('Error adding note:', error);
        this.notifyError(this.$t('backoffice.moderation.note-error') || 'Error adding note');
      }
    },

    // Real-time updates
    setupRealTimeUpdates() {
      // Disabled automatic polling to prevent database overload
      // Users can manually refresh using the refresh button
      console.log('Real-time updates disabled to prevent database overload');
    },

    stopRealTimeUpdates() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },

    // Moderator assignment methods
    async updateModeratorAssignment(item, moderatorKey) {
      try {
        const startTime = Date.now();
        
        // Call the API to update moderator assignment
        const response = await this.$store.dispatch('editItem', {
          item: {
            itemType: 'moderationAssignment',
            requestConfig: {
              moderationId: item.id,
              moderatorKey: moderatorKey,
              contentType: item.contentType,
              contentKey: item.contentKey
            }
          },
          noSet: true
        });

        // Update local data
        const itemIndex = this.moderationQueue.findIndex(i => i.id === item.id);
        if (itemIndex !== -1) {
          if (moderatorKey) {
            const moderator = this.staffMembers.find(m => m.key === moderatorKey);
            this.moderationQueue[itemIndex].assignedModerator = moderator;
            this.moderationQueue[itemIndex].assignedModeratorKey = moderatorKey;
          } else {
            this.moderationQueue[itemIndex].assignedModerator = null;
            this.moderationQueue[itemIndex].assignedModeratorKey = null;
          }
        }

        // Track performance
        const duration = Date.now() - startTime;
        this.trackPerformance('updateModeratorAssignment', duration);

        this.notifySuccess(
          moderatorKey 
            ? (this.$t('backoffice.moderation.moderator-assigned-success') || 'Moderator assigned successfully')
            : (this.$t('backoffice.moderation.moderator-unassigned-success') || 'Moderator unassigned successfully')
        );

        // Update moderator workload
        await this.calculateModeratorWorkload();
        
      } catch (error) {
        console.error('Error updating moderator assignment:', error);
        this.notifyError(this.$t('backoffice.moderation.moderator-assign-error') || 'Error assigning moderator');
      }
    },

    // Moderator filtering methods
    filterByModerator(moderatorKey) {
      this.filters.moderatedBy = moderatorKey;
      this.applyFilters();
    },

    async calculateModeratorWorkload() {
      try {
        const workload = [];
        
        for (const moderator of this.staffMembers) {
          const pending = this.moderationQueue.filter(item => 
            item.assignedModeratorKey === moderator.key && 
            item.moderationStatus === 'pending'
          ).length;
          
          const completed = this.moderationQueue.filter(item => 
            item.assignedModeratorKey === moderator.key && 
            (item.moderationStatus === 'approved' || item.moderationStatus === 'rejected')
          ).length;

          workload.push({
            moderatorKey: moderator.key,
            moderator: moderator,
            pending: pending,
            completed: completed,
            total: pending + completed
          });
        }

        this.moderatorWorkload = workload;
      } catch (error) {
        console.error('Error calculating moderator workload:', error);
        this.moderatorWorkload = [];
      }
    },

    // Enhanced fetch methods with moderator data
    async fetchModerationQueueEnhanced() {
      try {
        // Filter out empty values from filters
        const cleanFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => 
            value !== '' && value !== null && value !== undefined
          )
        );
        
        const response = await this.$store.dispatch('getItems', {
          itemType: 'moderation',
          storedKey: 'queue',
          forceAPICall: true,
          page: this.pagination.currentPage,
          perPage: this.pagination.perPage,
          requestConfig: {
            ...cleanFilters,
            count: this.pagination.perPage,
            offset: (this.pagination.currentPage - 1) * this.pagination.perPage,
            includeModeratorInfo: true
          }
        });

        this.moderationQueue = response?.data || [];
        this.pagination.total = response?.total || 0;
        
        // Calculate stats from the fetched data
        this.calculateModerationStats();
        
        // Calculate moderator workload after fetching data
        await this.calculateModeratorWorkload();
        
        // Calculate content type statistics
        await this.calculateContentTypeStats();
        
        console.log('Fetched moderation queue:', this.moderationQueue.length);
      } catch (error) {
        console.error('Error fetching moderation queue:', error);
        this.moderationQueue = [];
        this.pagination.total = 0;
        
        // Handle specific error types
        if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
          this.notifyError(this.$t('backoffice.moderation.network-error') || 'Network error. Please check your connection and try again.');
        } else if (error.response?.status === 503) {
          this.notifyError(this.$t('backoffice.moderation.service-unavailable') || 'Moderation service is temporarily unavailable. Please try again later.');
        } else if (error.response?.status === 403) {
          this.notifyError(this.$t('backoffice.moderation.access-denied') || 'Access denied. You may not have permission to access moderation data.');
        } else {
          this.notifyError(this.$t('backoffice.moderation.fetch-error') || 'Error fetching moderation data. Please try again.');
        }
      }
    },

    // Content type statistics methods
    async calculateContentTypeStats() {
      try {
        const stats = [];
        // Only include currently implemented content types
        const contentTypes = [
          'questions', 'answers', 'posts', 'comments'
        ];
        // TODO: Add more content types when frontend implementation is ready
        // 'articles', 'blogs', 'challenges', 'chats', 'products', 'links',
        // 'organizations', 'communities', 'events', 'ideas', 'jobs', 'work_items',
        // 'tenders', 'media', 'entities', 'people', 'polls'

        for (const contentType of contentTypes) {
          const total = this.moderationQueue.filter(item => item.contentType === contentType).length;
          const pending = this.moderationQueue.filter(item => 
            item.contentType === contentType && item.moderationStatus === 'pending'
          ).length;

          if (total > 0) {
            stats.push({
              type: contentType,
              count: total,
              pending: pending,
              completed: total - pending
            });
          }
        }

        // Sort by count (descending)
        this.contentTypeStats = stats.sort((a, b) => b.count - a.count);
      } catch (error) {
        console.error('Error calculating content type stats:', error);
        this.contentTypeStats = [];
      }
    },

    filterByContentType(contentType) {
      this.filters.contentType = contentType;
      this.applyFilters();
    }
  }
};
</script>

<style scoped>
.moderation-dashboard {
  width: 100%;
}

.moderation-item {
  background-color: #f8f9fa;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.moderation-item:hover {
  background-color: #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.moderation-item.selected-item {
  border-color: #007bff;
  background-color: #e3f2fd;
}

.content-preview {
  max-height: 200px;
  overflow: hidden;
  position: relative;
}

.content-preview::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(transparent, #f8f9fa);
}

.content-preview img {
  max-width: 100%;
  height: auto;
}

.moderator-info {
  background-color: #f8f9fa;
}

.form-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

/* Statistics cards */
.statistics-card {
  border-left: 4px solid;
}

.statistics-card.pending {
  border-left-color: #ffc107;
}

.statistics-card.approved {
  border-left-color: #28a745;
}

.statistics-card.rejected {
  border-left-color: #dc3545;
}

.statistics-card.moderators {
  border-left-color: #17a2b8;
}

/* Bulk actions */
.bulk-actions {
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 4px;
  padding: 8px 12px;
}

/* Filter section */
.filter-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .moderation-item {
    padding: 12px;
  }
  
  .bulk-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .statistics-card {
    margin-bottom: 12px;
  }
}

/* Animation for selection */
.moderation-item {
  transition: all 0.3s ease;
}

.moderation-item.selected-item {
  transform: translateX(4px);
}

/* Loading states */
.loading-overlay {
  position: relative;
}

.loading-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Performance metrics */
.metric-item {
  padding: 12px;
  border-radius: 8px;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.metric-item:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
}

/* Moderation history */
.moderation-history {
  max-height: 300px;
  overflow-y: auto;
}

.history-item {
  border-radius: 4px;
  transition: all 0.2s ease;
}

.history-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Pagination improvements */
.pagination {
  margin-bottom: 0;
}

.pagination .page-link {
  border-radius: 4px;
  margin: 0 2px;
  border: 1px solid #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
}

/* Real-time indicators */
.real-time-indicator {
  position: relative;
}

.real-time-indicator::after {
  content: '';
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background-color: #28a745;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
}

/* Enhanced accessibility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Focus states */
.moderation-item:focus-within {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* Print styles */
@media print {
  .bulk-actions,
  .pagination,
  .filter-section {
    display: none !important;
  }
  
  .moderation-item {
    break-inside: avoid;
    margin-bottom: 20px;
  }
}

/* Moderator assignment styles */
.moderator-assignment {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.moderator-assignment:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.moderator-selector {
  min-width: 200px;
}

.moderator-selector .form-control {
  border-radius: 6px;
  border: 1px solid #ced4da;
  transition: all 0.2s ease;
}

.moderator-selector .form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Moderator workload styles */
.workload-item {
  transition: all 0.2s ease;
  cursor: pointer;
}

.workload-item:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.workload-item.active-moderator {
  background-color: #e3f2fd;
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.2);
}

/* Moderator card enhancements */
.moderator-card {
  transition: all 0.2s ease;
  border: 1px solid #dee2e6;
}

.moderator-card:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transform: translateY(-1px);
}

/* Status indicators for moderator assignments */
.moderator-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.moderator-status.assigned {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.moderator-status.unassigned {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

/* Responsive moderator section */
@media (max-width: 768px) {
  .moderator-assignment {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .moderator-selector {
    width: 100%;
    margin-top: 8px;
  }
  
  .workload-item {
    padding: 8px;
  }
  
  .workload-item .d-flex {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .workload-item .text-right {
    margin-top: 8px;
    text-align: left;
  }
}

/* Animation for moderator assignment changes */
.moderator-assignment-updated {
  animation: highlightChange 0.5s ease-in-out;
}

@keyframes highlightChange {
  0% {
    background-color: #d4edda;
  }
  50% {
    background-color: #c3e6cb;
  }
  100% {
    background-color: #f8f9fa;
  }
}

/* Content type statistics styles */
.content-type-stat {
  transition: all 0.2s ease;
  cursor: pointer;
}

.content-type-stat:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.content-type-stat.active {
  background-color: #e3f2fd;
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.2);
}

/* Content type badge variants */
.badge-primary { background-color: #007bff; }
.badge-info { background-color: #17a2b8; }
.badge-success { background-color: #28a745; }
.badge-secondary { background-color: #6c757d; }
.badge-warning { background-color: #ffc107; color: #212529; }
.badge-dark { background-color: #343a40; }
.badge-danger { background-color: #dc3545; }
.badge-light { background-color: #f8f9fa; color: #212529; }
</style>
